import * as React from 'react';
import { ISOTIPO, WORDMARK } from './brand-logo-paths';
import { cn } from '../../lib';

/**
 * Marca «La agencia»: único componente para mostrar el logo en la interfaz.
 *
 * - `variant`: `isotipo` (cuadrado dorado con «La»), `wordmark` («La» negrita con línea dorada +
 *   «agencia» regular) o `completo` (ambos, en horizontal).
 * - `tone`: `oscuro` para fondos claros (texto `brand-black`) o `claro` para fondos oscuros
 *   (texto `brand-white`). El dorado del logo (`brand-logo-gold`) y el blanco del isotipo no cambian.
 * - Accesibilidad: el contenedor es `role="img"` con `aria-label` («La agencia» por defecto); los
 *   SVG internos son decorativos (`aria-hidden`).
 */
export type BrandLogoVariant = 'isotipo' | 'completo' | 'wordmark';
export type BrandLogoSize = 'sm' | 'md' | 'lg' | 'xl';
export type BrandLogoTone = 'oscuro' | 'claro';

export interface BrandLogoProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children' | 'role'> {
  variant?: BrandLogoVariant;
  size?: BrandLogoSize;
  tone?: BrandLogoTone;
}

const SIZE: Record<BrandLogoSize, { isotipo: string; wordmark: string; gap: string }> = {
  sm: { isotipo: 'h-8', wordmark: 'h-4', gap: 'gap-2' },
  md: { isotipo: 'h-10', wordmark: 'h-5', gap: 'gap-2.5' },
  lg: { isotipo: 'h-14', wordmark: 'h-7', gap: 'gap-3' },
  xl: { isotipo: 'h-20', wordmark: 'h-10', gap: 'gap-4' },
};

const TONE: Record<BrandLogoTone, string> = {
  oscuro: 'text-brand-black',
  claro: 'text-brand-white',
};

const decorative = { 'aria-hidden': true, focusable: 'false' } as const;

const Isotipo = ({ className }: { className: string }) => (
  <svg
    {...decorative}
    data-brand-part="isotipo"
    viewBox={ISOTIPO.viewBox}
    className={cn('w-auto shrink-0', className)}
  >
    <rect data-brand-shape="fondo" width={ISOTIPO.width} height={ISOTIPO.height} className="fill-brand-logo-gold" />
    <g className="fill-brand-white">
      <path fillRule="evenodd" d={ISOTIPO.marcos} />
      <path d={ISOTIPO.macizos} />
      <path fillRule="evenodd" d={ISOTIPO.letraA} />
    </g>
  </svg>
);

const Wordmark = ({ className }: { className: string }) => (
  <svg
    {...decorative}
    data-brand-part="wordmark"
    viewBox={WORDMARK.viewBox}
    className={cn('w-auto shrink-0', className)}
  >
    <path data-brand-shape="la" d={WORDMARK.la} fill="currentColor" />
    <path data-brand-shape="agencia" d={WORDMARK.agencia} fill="currentColor" />
    <rect data-brand-shape="linea" {...WORDMARK.linea} className="fill-brand-logo-gold" />
  </svg>
);

const BrandLogo = React.forwardRef<HTMLSpanElement, BrandLogoProps>(
  (
    { variant = 'completo', size = 'md', tone = 'oscuro', className, 'aria-label': ariaLabel = 'La agencia', ...props },
    ref,
  ) => {
    const s = SIZE[size];
    return (
      <span
        ref={ref}
        role="img"
        aria-label={ariaLabel}
        data-variant={variant}
        data-size={size}
        data-tone={tone}
        className={cn('inline-flex shrink-0 items-center', s.gap, className)}
        {...props}
      >
        {variant !== 'wordmark' && <Isotipo className={s.isotipo} />}
        {variant !== 'isotipo' && <Wordmark className={cn(s.wordmark, TONE[tone])} />}
      </span>
    );
  },
);
BrandLogo.displayName = 'BrandLogo';

export { BrandLogo };
